import React from 'react';
import {toast} from 'react-toastify';
import usePageTitle from '../../../../hooks/usePageTitle';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import { useBreadcrumbs } from '../../../../contexts/BreadcrumbContext';
import {
  selectAuthIsForecaster,
  selectAuthIsMarketOwner,
  selectAuthIsAdmin,
  selectAuthIsMarketOwnerReadOnly
} from '../../../../slices/authSlice';
import PermissionChecker from '../../../../utils/permissionChecker';
import {useDispatch, useSelector} from 'react-redux';
import {
  selectSelectedResource,
  setSelectedResource as setGlobalSelectedResource
} from '../../../../slices/preferencesSlice';

// Custom hooks
import {useFilesState} from './hooks/useFilesState';
import {useFilesData} from './hooks/useFilesData';
import {useFileActions} from './hooks/useFileActions';
import {validateExportRequest} from './utils/exportValidation';

// Components
import {UploadSection} from './components/UploadSection';
import {ExportRequestSection} from './components/ExportRequestSection';
import {FilesGrid} from './components/FilesGrid';
import {ExportsList} from './components/ExportsList';

export default function FilesPage() {
  usePageTitle('Files');
  useBreadcrumbs([{ label: 'Files' }], 'Browse and manage uploaded data files');
  const dispatch = useDispatch();

  // Auth selectors
  const isForecaster = useSelector(selectAuthIsForecaster);
  const isMarketOwner = useSelector(selectAuthIsMarketOwner);
  const isAdmin = useSelector(selectAuthIsAdmin);
  const isMarketOwnerReadOnly = useSelector(selectAuthIsMarketOwnerReadOnly);
  const permissionChecker = new PermissionChecker({isAdmin, isMarketOwner, isMarketOwnerReadOnly, isForecaster});

  // Global selected resource
  const globalSelectedResource = useSelector(selectSelectedResource);

  // Permission checks
  const showUpload = !isForecaster;
  const isUploadEnabled = permissionChecker.satisfies(['market_owner', 'admin']);
  const showTargetUserSelect = permissionChecker.satisfies(['market_owner_read', 'market_owner', 'admin']);

  // Local UI state hook
  const state = useFilesState(globalSelectedResource, null);

  // Data fetching hook
  const {
    filtered,
    scopeCounts,
    sortedExports,
    availableResources,
    participants,
    availableHorizons,
    availableHorizonsLoading,
  } = useFilesData(state.scope, state.selectedResource, state.search, showTargetUserSelect);

  // File actions hook
  const actions = useFileActions(state.selectedResource, globalSelectedResource);

  // Export confirmation dialog state
  const [exportConfirmOpen, setExportConfirmOpen] = React.useState(false);

  // Handle refresh exports
  const handleRefreshExports = async () => {
    await actions.refreshExports();
  };

   // Handle file edit
  const handleEdit = (file) => {
    actions.setEditingFile(file.raw);
    actions.setFileName(file.name);
    actions.setPickedFile(null);
    if (file.resourceId) {
      state.setScope('resource');
      state.setSelectedResource(file.resourceId);
      dispatch(setGlobalSelectedResource(file.resourceId));
    }
  };

  // Submit export request - shows confirmation dialog first
  const submitForecastRequest = async () => {
    const error = validateExportRequest({
      scope: state.scope,
      selectedResource: state.selectedResource,
      showTargetUserSelect,
      targetUserId: state.targetUserId,
      exportType: state.exportType,
      selectedHorizon: state.selectedHorizon,
      exportStartDate: state.exportStartDate,
      exportEndDate: state.exportEndDate,
    });
    if (error) {
      state.setExportMessage(error);
      toast.error(error);
      return;
    }
    // Show confirmation dialog
    setExportConfirmOpen(true);
  };

  // Actually submit the export request after confirmation
  const confirmExportRequest = async () => {
    setExportConfirmOpen(false);

    const payload = {
      resource: globalSelectedResource?.id || null,
      start_date: state.exportStartDate,
      end_date: state.exportEndDate,
      horizon: state.selectedHorizon,
      metrics: null,
      user: state.targetUserId || null
    };

    // Add include_ensemble and anonymize parameters for market reports
    if (state.exportType === 'market') {
      payload.include_ensemble = state.includeEnsemble;
      payload.anonymize = state.anonymize;
      // ensemble_model only affects the report when ensemble scores are included
      if (state.includeEnsemble) {
        payload.ensemble_model = state.ensembleModel;
      }
      // Omit when 'all' so default behavior is unchanged
      if (state.paymentFilter && state.paymentFilter !== 'all') {
        payload.payment_filter = state.paymentFilter;
      }
    }

    // Raw measurement exports only need resource + date range.
    if (state.exportType === 'raw_measurement') {
      delete payload.horizon;
      delete payload.metrics;
      delete payload.user;
    }

    // Pass the exportType to dispatch to the correct endpoint
    await actions.submitExportRequest(payload, state.setExportMessage, state.exportType);
  };


  return (
    <div className="space-y-6">
      <ConfirmDialog
        open={actions.deleteOpen}
        title="Delete File"
        description={`Are you sure you want to delete "${actions.deleteTarget?.name || 'this file'}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onCancel={() => actions.setDeleteOpen(false)}
        onConfirm={actions.performDelete}
        iconBg="bg-red-100"
        iconColor="text-red-600"
        requireTextConfirmation={true}
        confirmationText={actions.deleteTarget?.name || ''}
        confirmationPlaceholder="Type the file name to confirm"
      />

      <ConfirmDialog
        open={exportConfirmOpen}
        title="Request Data Export"
        description="Please note that export files will only be available temporarily for 76 hours. After this period, the files will be automatically deleted from the system."
        confirmText="Request Export"
        cancelText="Cancel"
        onCancel={() => setExportConfirmOpen(false)}
        onConfirm={confirmExportRequest}
        iconBg="bg-blue-100"
        iconColor="text-blue-600"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column */}
        <div className="lg:col-span-1 flex flex-col">
          <div className="rounded-lg bg-surface dark:bg-gray-870 shadow-sm border border-gray-200/70 dark:border-gray-600/70 divide-y divide-gray-100 dark:divide-gray-700 flex-1 flex flex-col">
            {showUpload && (
              <UploadSection
                fileName={actions.fileName}
                setFileName={actions.setFileName}
                pickedFile={actions.pickedFile}
                editingFile={actions.editingFile}
                isUploadEnabled={isUploadEnabled}
                onFilePick={actions.handleFilePick}
                onUpload={(e) => actions.handleUpload(e, state.scope)}
                onClear={actions.resetForm}
              />
            )}

            <ExportRequestSection
              exportType={state.exportType}
              setExportType={state.setExportType}
              exportStartDate={state.exportStartDate}
              setExportStartDate={state.setExportStartDate}
              exportEndDate={state.exportEndDate}
              setExportEndDate={state.setExportEndDate}
              exportMessage={state.exportMessage}
              showTargetUserSelect={showTargetUserSelect}
              isMarketOwnerOrAdmin={isUploadEnabled}
              selectedResource={state.selectedResource}
              targetUserId={state.targetUserId}
              setTargetUserId={state.setTargetUserId}
              selectedHorizon={state.selectedHorizon}
              setSelectedHorizon={state.setSelectedHorizon}
              availableHorizons={availableHorizons}
              availableHorizonsLoading={availableHorizonsLoading}
              includeEnsemble={state.includeEnsemble}
              setIncludeEnsemble={state.setIncludeEnsemble}
              ensembleModel={state.ensembleModel}
              setEnsembleModel={state.setEnsembleModel}
              anonymize={state.anonymize}
              setAnonymize={state.setAnonymize}
              paymentFilter={state.paymentFilter}
              setPaymentFilter={state.setPaymentFilter}
              participants={participants}
              onSubmit={submitForecastRequest}
              onReset={() => {
                state.setExportStartDate('');
                state.setExportEndDate('');
                state.setExportMessage('');
                state.setIncludeEnsemble(false);
                state.setEnsembleModel('weighted_avg');
                state.setAnonymize(false);
                state.setPaymentFilter('all');
                state.setSelectedHorizon(null);
              }}
            />
          </div>
        </div>

        {/* Right Column */}
        <div className="lg:col-span-2 flex flex-col gap-4">
          <FilesGrid
            filtered={filtered}
            showUpload={showUpload}
            scope={state.scope}
            setScope={state.setScope}
            scopeCounts={scopeCounts}
            resourceName={globalSelectedResource?.name}
            filesPage={state.filesPage}
            setFilesPage={state.setFilesPage}
            filesPerPage={state.filesPerPage}
            setFilesPerPage={state.setFilesPerPage}
            onDownload={actions.handleDownload}
            onEdit={handleEdit}
            onDelete={actions.confirmDelete}
          />

          <ExportsList
            sortedDisplay={sortedExports}
            availableResources={availableResources}
            exportsPage={state.exportsPage}
            setExportsPage={state.setExportsPage}
            exportsPerPage={state.exportsPerPage}
            setExportsPerPage={state.setExportsPerPage}
            exportsFilterType={state.exportsFilterType}
            setExportsFilterType={state.setExportsFilterType}
            onDownload={actions.handleExportDownload}
            onRefresh={handleRefreshExports}
            isRefreshing={actions.isRefreshing}
            isAdmin={isAdmin}
            isMarketOwner={isMarketOwner}
          />
        </div>
      </div>
    </div>
  );
}

